import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import classNames from 'classnames/bind';
import { FLAG_COMPONENTS } from '../../../../common/flags/FlagIcons';
import { useGetNotificationUnreadCountQuery } from '../../../../../api/notificationsApi';
import { usePollingInterval } from '../../../../../hooks/usePollingInterval';
import { useLanguageSync } from '../../../../../translations/useLanguageSync';
import NotificationDropdown from './NotificationDropdown';
import SearchDropdown from './SearchDropdown';
import ThemeToggle from '../../../../theme-toggle';
import styles from './TopBar.module.scss';

const cx = classNames.bind(styles);

interface User {
  id: number;
  name: string;
  role: string;
  avatar?: string | null;
  first_name?: string | null;
  last_name?: string | null;
}

interface TopBarProps {
  user: User | null;
  onLogout: () => void;
  onMenuToggle: () => void;
}

const TopBar: React.FC<TopBarProps> = ({ user, onLogout, onMenuToggle }) => {
  const { t } = useTranslation();
  const { currentLanguage: currentLangCode, changeLanguage } = useLanguageSync();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);
  const notifRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const pollInterval = usePollingInterval(60000);
  const { data: unreadData } = useGetNotificationUnreadCountQuery(undefined, {
    pollingInterval: pollInterval,
  });
  const unreadCount = unreadData?.unread_count ?? 0;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setShowNotificationDropdown(false);
      }
    };
    if (showNotificationDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNotificationDropdown]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setShowLanguageDropdown(false);
      }
    };
    if (showLanguageDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showLanguageDropdown]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };
    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu]);

  // Debounce search input by 300ms
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      if (searchQuery.length >= 2) {
        setShowSearchDropdown(true);
      } else {
        setShowSearchDropdown(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Close search dropdown on navigation
  useEffect(() => {
    setShowSearchDropdown(false);
    setSearchQuery('');
  }, [location.pathname]);

  // Detect if the page title is truncated and hide it completely if so
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [isTitleTruncated, setIsTitleTruncated] = useState(false);

  useEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    const check = () => setIsTitleTruncated(el.scrollWidth > el.clientWidth + 2);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [location.pathname]);

  const isAdmin = user?.role === 'ADMIN';
  const isCurator = user?.role === 'CURATOR';
  const prefix = isAdmin ? '/admin' : isCurator ? '/curator' : '';

  const getPageTitle = (): string => {
    const path = location.pathname;
    if (path === '/dashboard' || path === '/') return t('topbar.dashboard', 'Dashboard');
    if (path === '/courses') return t('topbar.courses', 'Courses');
    if (path === '/schedule') return t('topbar.schedule', 'Schedule');
    if (path === '/instructors') return t('topbar.instructors', 'Instructors');
    if (path === '/profile') return t('topbar.profile', 'Profile');
    if (path === '/settings') return t('topbar.settings', 'Settings');
    if (path === '/messages') return t('topbar.messages', 'Messages');
    if (path === '/vocabulary') return t('topbar.vocabulary', 'Vocabulary');
    if (path === '/learner/pay') return t('topbar.payment', 'Payment');
    if (path === '/exercises') return t('topbar.exercises', 'Exercises');
    if (path.startsWith('/exercises/')) return t('topbar.exercises', 'Exercises');
    if (path === '/catalog-exercise') return t('topbar.exerciseCatalog', 'Exercise Catalog');
    // Admin routes
    if (path === '/admin/profile') return t('topbar.profile', 'Profile');
    if (path === '/admin/settings') return t('topbar.settings', 'Settings');
    if (path === '/admin') return t('topbar.dashboard', 'Dashboard');
    if (path === '/admin/users') return t('topbar.students', 'Students');
    if (path === '/admin/courses') return t('topbar.courses', 'Courses');
    if (path === '/admin/schedule') return t('topbar.schedule', 'Schedule');
    if (path === '/admin/resources') return t('topbar.resource', 'Resource');
    if (path === '/admin/transactions') return t('topbar.transaction', 'Transaction');
    if (path === '/admin/messages') return t('topbar.messages', 'Messages');
    if (path === '/admin/vocabulary') return t('topbar.vocabulary', 'Vocabulary');
    if (path === '/admin/live-class') return t('topbar.liveClass', 'Live Class');
    if (path === '/admin/ai-costs') return t('sidebar.aiCosts', 'AI Costs');
    if (path === '/admin/logs') return t('sidebar.logs', 'Logs');
    if (path === '/admin/translations') return t('sidebar.translations', 'Translations');
    if (path.startsWith('/admin/exercises-constructor')) return t('topbar.exerciseConstructor', 'Exercise Constructor');
    if (path === '/admin/exercise-generator') return t('topbar.exerciseGenerator', 'Exercise Generator');
    if (path.startsWith('/admin/course')) return t('topbar.courseManagement', 'Course Management');
    if (path.startsWith('/admin/users')) return t('topbar.students', 'Students');
    if (path.startsWith('/admin/messages')) return t('topbar.messages', 'Messages');
    if (path.startsWith('/admin')) return t('topbar.admin', 'Admin Panel');
    // Learner routes
    if (path.startsWith('/courses/') || path.startsWith('/course/')) return t('topbar.course', 'Course');
    // Curator routes
    if (path === '/curator' || path === '/curator/users') return t('topbar.students', 'Students');
    if (path === '/curator/messages') return t('topbar.messages', 'Messages');
    if (path === '/curator/profile') return t('topbar.profile', 'Profile');
    if (path === '/curator/settings') return t('topbar.settings', 'Settings');
    if (path.startsWith('/curator')) return t('topbar.curator', 'Curator Panel');
    return t('topbar.dashboard', 'Dashboard');
  };

  const languages = [
    { code: 'en', label: 'English', shortLabel: 'Eng (US)' },
    { code: 'de', label: 'Deutsch', shortLabel: 'Deu' },
    { code: 'uk', label: 'Українська', shortLabel: 'Укр' },
    { code: 'ru', label: 'Русский', shortLabel: 'Рус' },
  ];

  const currentLanguage = languages.find((l) => l.code === currentLangCode) || languages[0];

  const handleLanguageChange = (code: string) => {
    changeLanguage(code);
    setShowLanguageDropdown(false);
  };

  return (
    <header className={cx('topbar')}>
      <div className={cx('topbarInner')}>
        <div className={cx('leftSection')}>
          <button className={cx('menuButton')} onClick={onMenuToggle} aria-label={t('topbar.toggleMenu', 'Toggle menu')}>
            <i className="fas fa-bars"></i>
          </button>
          <h1 className={cx('pageTitle', { pageTitleHidden: isTitleTruncated })} ref={titleRef}>{getPageTitle()}</h1>
        </div>

        <div className={cx('rightSection')}>
        <div className={cx('searchWrapper')} ref={searchRef} data-tour="topbar-search">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder={t('topbar.searchHere', 'Search here...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => { if (debouncedSearch.length >= 2) setShowSearchDropdown(true); }}
            className={cx('searchInput')}
          />
          {showSearchDropdown && (
            <div className="searchDropdown">
              <SearchDropdown
                query={debouncedSearch}
                onClose={() => { setShowSearchDropdown(false); setSearchQuery(''); }}
                userRole={user?.role || 'STUDENT'}
              />
            </div>
          )}
        </div>

        <div className={cx('languageSelector')} ref={langRef} data-tour="topbar-language">
          <button
            className={cx('languageButton')}
            onClick={() => setShowLanguageDropdown(!showLanguageDropdown)}
          >
            <span className={cx('flag')}>{FLAG_COMPONENTS[currentLanguage.code]?.({ size: 24 })}</span>
            <span className={cx('langLabel')}>{currentLanguage.shortLabel}</span>
            <i className="fas fa-chevron-down"></i>
          </button>
          {showLanguageDropdown && (
            <div className={cx('languageDropdown')}>
              {languages.map((lang) => (
                <button
                  key={lang.code}
                  className={cx('languageOption', { active: lang.code === currentLangCode })}
                  onClick={() => handleLanguageChange(lang.code)}
                >
                  <span className={cx('flag')}>{FLAG_COMPONENTS[lang.code]?.({ size: 20 })}</span>
                  <span>{lang.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <Link className={cx('iconButton')} to={`${prefix}/messages`} aria-label={t('topbar.messages', 'Messages')}>
          <i className="fas fa-comment-dots"></i>
        </Link>

        <div className={cx('notificationWrapper')} ref={notifRef}>
          <button
            className={cx('iconButton', 'notificationButton')}
            onClick={() => setShowNotificationDropdown(!showNotificationDropdown)}
            aria-label={t('topbar.notifications', 'Notifications')}
          >
            <i className="fas fa-bell"></i>
            {unreadCount > 0 && (
              <span className={cx('badge')}>
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
          {showNotificationDropdown && (
            <NotificationDropdown onClose={() => setShowNotificationDropdown(false)} />
          )}
        </div>

        <Link className={cx('iconButton')} to={`${prefix}/settings`} aria-label={t('topbar.settings', 'Settings')}>
          <i className="fas fa-cog"></i>
        </Link>

        <ThemeToggle className={cx('themeToggleBtn')} />

        <div className={cx('userMenu')} ref={userMenuRef}>
          <button
            className={cx('userButton')}
            onClick={() => setShowUserMenu(!showUserMenu)}
          >
            <div className={cx('avatar')}>
              {user?.avatar ? (
                <img src={user.avatar} alt="" referrerPolicy="no-referrer" />
              ) : (
                <i className="fas fa-user"></i>
              )}
            </div>
            <div className={cx('userInfo')}>
              <span className={cx('userName')}>{user?.first_name || user?.name || t('topbar.user', 'User')}</span>
              <span className={cx('userRole')}>{user?.role === 'ADMIN' ? t('settings.roleAdmin', 'Administrator') : user?.role === 'CURATOR' ? t('settings.roleCurator', 'Curator') : t('settings.roleStudent', 'Student')}</span>
            </div>
            <i className="fas fa-chevron-down"></i>
          </button>
          {showUserMenu && (
            <div className={cx('userDropdown')}>
              <Link className={cx('dropdownItem')} to={`${prefix}/profile`} onClick={() => setShowUserMenu(false)}>
                <i className="fas fa-user"></i>
                <span>{t('topbar.profile', 'Profile')}</span>
              </Link>
              <Link className={cx('dropdownItem')} to={`${prefix}/settings`} onClick={() => setShowUserMenu(false)}>
                <i className="fas fa-cog"></i>
                <span>{t('topbar.settings', 'Settings')}</span>
              </Link>
              <hr className={cx('divider')} />
              <button className={cx('dropdownItem', 'logout')} onClick={onLogout}>
                <i className="fas fa-sign-out-alt"></i>
                <span>{t('topbar.logout', 'Logout')}</span>
              </button>
            </div>
          )}
        </div>
      </div>
      </div>
    </header>
  );
};

export default TopBar;
